import { CSS_PX_PER_PT } from '@messenger/consts'
import type { EditorTextBoxInput } from '@messenger/lotus/row-store'
import { cssColorToHex } from '../cssColorToHex'
import { asNumber, asStored } from '../editorTextBoxSave'
import {
  EditorTextBoxConversionError,
  type EditorTextBoxConversionReportCollector,
} from './report'

/**
 * The TinyMCE text item a conversion reads: its stored HTML, its frame and
 * state, and the edits that styled it. Every field is read defensively,
 * since the page JSON holds numbers as strings as often as numbers.
 */
export type EditorTextBoxTinyMceItem = {
  id?: unknown
  x?: unknown
  y?: unknown
  z?: unknown
  width?: unknown
  height?: unknown
  assetLocked?: unknown
  hideInPdf?: unknown
  text?: { value?: unknown } | null
  edits?: {
    rotate?: unknown
    textShadow?: unknown
    dropShadow?: unknown
    opacity?: unknown
    backgroundColor?: unknown
    borderThickness?: unknown
    borderColor?: unknown
    borderRadius?: unknown
  } | null
}

export type TinyMceBoxColumns = Omit<EditorTextBoxInput, 'blocks'>

type TinyMceShadow = {
  xOffset?: unknown
  yOffset?: unknown
  blur?: unknown
  spread?: unknown
  color?: unknown
}

const shadowOf = (value: unknown): TinyMceShadow | null =>
  typeof value === 'object' && value !== null ? (value as TinyMceShadow) : null

const pointsOf = (px: number): number => asStored(px / CSS_PX_PER_PT)

/**
 * The item's text shadow as the box's: TinyMCE painted
 * `text-shadow: <x>px <y>px <blur>px <color>` and never read the stored
 * opacity, so the box's opacity is 1. A shadow TinyMCE could not paint (a
 * missing or non-numeric length, a negative blur, no colour) is not one to
 * carry; a colour the box cannot store, a named one, skips the shadow with a
 * report.
 */
const textShadowOf = (
  value: unknown,
  report: EditorTextBoxConversionReportCollector,
): EditorTextBoxInput['textShadow'] => {
  const shadow = shadowOf(value)
  if (shadow === null) return undefined
  const x = asNumber(shadow.xOffset)
  const y = asNumber(shadow.yOffset)
  const blur = asNumber(shadow.blur)
  const color = typeof shadow.color === 'string' ? shadow.color.trim() : ''
  if (x === null || y === null || blur === null || blur < 0 || color === '')
    return undefined
  const hex = cssColorToHex(color)
  if (hex === null) {
    report.add({ kind: 'shadow-skipped', color })
    return undefined
  }
  return {
    x: pointsOf(x),
    y: pointsOf(y),
    blur: pointsOf(blur),
    color: hex,
    opacity: 1,
  }
}

const isTransparent = (color: string): boolean =>
  color === '' || color.toLowerCase() === 'transparent'

/** The edits TinyMCE painted on the box itself that a conversion leaves behind; of the box's own edits only the text shadow carries. */
const reportDroppedEdits = (
  edits: NonNullable<EditorTextBoxTinyMceItem['edits']>,
  report: EditorTextBoxConversionReportCollector,
): void => {
  const background =
    typeof edits.backgroundColor === 'string'
      ? edits.backgroundColor.trim()
      : ''
  if (!isTransparent(background))
    report.add({ kind: 'edit-dropped', edit: 'background' })
  const opacity = asNumber(edits.opacity)
  if (opacity !== null && opacity < 1)
    report.add({ kind: 'edit-dropped', edit: 'opacity' })
  const border = asNumber(edits.borderThickness)
  if (border !== null && border > 0)
    report.add({ kind: 'edit-dropped', edit: 'border' })
  const drop = shadowOf(edits.dropShadow)
  if (
    drop !== null &&
    [drop.xOffset, drop.yOffset, drop.blur, drop.spread].some(
      (length) => (asNumber(length) ?? 0) !== 0,
    )
  )
    report.add({ kind: 'edit-dropped', edit: 'drop-shadow' })
}

const requiredNumber = (value: unknown, name: string): number => {
  const n = asNumber(value)
  if (n === null)
    throw new EditorTextBoxConversionError(
      'geometry',
      `the TinyMCE item's ${name} is not a number`,
    )
  return asStored(n)
}

/**
 * The box's own columns from the item: its id, frame, layer, rotation, lock
 * and PDF flag, and its text shadow; sizing stays unset. An item the row
 * store could not take as a box is refused by name here.
 */
export const tinyMceBoxColumns = (
  item: EditorTextBoxTinyMceItem,
  report: EditorTextBoxConversionReportCollector,
): TinyMceBoxColumns => {
  if (typeof item.id !== 'string' || item.id.trim() === '')
    throw new EditorTextBoxConversionError(
      'item-id',
      'the TinyMCE item has no id to give its box',
    )
  const x = requiredNumber(item.x, 'x')
  const y = requiredNumber(item.y, 'y')
  const width = requiredNumber(item.width, 'width')
  if (width <= 0)
    throw new EditorTextBoxConversionError(
      'width',
      `the TinyMCE item is ${width} px wide; a text box needs a positive width`,
    )
  const storedHeight = asNumber(item.height)
  if (storedHeight === null || storedHeight < 0)
    throw new EditorTextBoxConversionError(
      'height',
      'the TinyMCE item stores no height; pass the height its box paints at',
    )
  const edits = item.edits ?? {}
  const z = asNumber(item.z)
  const rotation = asNumber(edits.rotate)
  const textShadow = textShadowOf(edits.textShadow, report)
  reportDroppedEdits(edits, report)
  return {
    boxId: item.id,
    x,
    y,
    width,
    height: asStored(storedHeight),
    ...(z === null ? {} : { zIndex: Math.round(z) }),
    ...(rotation === null || asStored(rotation) === 0
      ? {}
      : { rotation: asStored(rotation) }),
    isLocked: item.assetLocked === true,
    isHiddenInPdf: item.hideInPdf === true,
    ...(textShadow === undefined ? {} : { textShadow }),
  }
}
